import { Head, useForm, usePage } from "@inertiajs/react"

import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import type { BreadcrumbItem, SharedData } from "@/types"

const CATEGORY_LABELS: Record<string, string> = {
  forum: "Foro",
  event: "Eventos",
  order: "Pedidos",
  raffle: "Sorteos",
  system: "Sistema",
  chat: "Chat",
}

type Preferences = Record<
  string,
  {
    email_enabled: boolean
    push_enabled: boolean
  }
>

interface PageProps {
  preferences: Preferences
  email_unsubscribed_all: boolean
  categories: string[]
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Notificaciones",
    href: "/settings/notifications",
  },
]

export default function NotificationsShow() {
  const {
    preferences,
    email_unsubscribed_all: emailUnsubscribedAll,
    categories,
  } = usePage<SharedData & PageProps>().props

  const { data, setData, patch, processing } = useForm({
    email_unsubscribed_all: emailUnsubscribedAll,
    preferences: { ...preferences },
  })

  const handleToggle = (
    category: string,
    field: "email_enabled" | "push_enabled",
    value: boolean,
  ) => {
    setData("preferences", {
      ...data.preferences,
      [category]: {
        ...data.preferences[category],
        [field]: value,
      },
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch("/settings/notifications")
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Notificaciones" />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Notificaciones"
            description="Configura cómo y cuándo recibir notificaciones"
          />

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Master unsubscribe toggle */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">
                  Desactivar todos los emails
                </Label>
                <p className="text-muted-foreground text-sm">
                  No recibir ningún email de notificaciones
                </p>
              </div>
              <Switch
                checked={data.email_unsubscribed_all}
                onCheckedChange={(checked: boolean) =>
                  setData("email_unsubscribed_all", checked)
                }
              />
            </div>

            {/* Per-category toggles */}
            <div className="space-y-4">
              <h3 className="text-muted-foreground text-sm font-medium">
                Por categoría
              </h3>
              {categories.map((category) => (
                <div
                  key={category}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex-1">
                    <Label className="text-sm font-medium">
                      {CATEGORY_LABELS[category] || category}
                    </Label>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        Email
                      </span>
                      <Switch
                        checked={
                          !data.email_unsubscribed_all &&
                          (data.preferences[category]?.email_enabled ?? true)
                        }
                        disabled={data.email_unsubscribed_all}
                        onCheckedChange={(checked: boolean) =>
                          handleToggle(category, "email_enabled", checked)
                        }
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-xs">
                        Push
                      </span>
                      <Switch
                        checked={
                          data.preferences[category]?.push_enabled ?? true
                        }
                        onCheckedChange={(checked: boolean) =>
                          handleToggle(category, "push_enabled", checked)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button type="submit" disabled={processing}>
              {processing ? "Guardando..." : "Guardar preferencias"}
            </Button>
          </form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
